import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, Shield, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase/client';
import TruckLogo from '../components/TruckLogo';

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [actionCode, setActionCode] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    number: false
  });
  
  // Extract the action code and email from URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const accessToken = queryParams.get('access_token');
    const refreshToken = queryParams.get('refresh_token');
    const type = queryParams.get('type');
    
    // Verify the tokens and set session
    const verifyTokens = async () => {
      try {
        if (accessToken && refreshToken && type === 'recovery') {
          // Set the session with the tokens
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (error) throw error;
          
          if (data.user?.email) {
            setEmail(data.user.email);
            setActionCode(accessToken); // Use access token as action code
          }
          
          setVerifying(false);
        } else {
          setVerificationError('No se encontraron los tokens de verificación en la URL');
          setVerifying(false);
        }
      } catch (error) {
        console.error('Error verificando tokens:', error);
        setVerificationError('El enlace de restablecimiento de contraseña no es válido o ha expirado');
        setVerifying(false);
      }
    };
    
    verifyTokens();
  }, [location]);
  
  // Check password strength as user types
  useEffect(() => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password)
    });
  }, [password]);
  
  // Password validation helper
  const isPasswordValid = (): boolean => {
    return passwordStrength.length && passwordStrength.uppercase && passwordStrength.number;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!isPasswordValid()) {
      setError('La contraseña no cumple con los requisitos de seguridad');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) throw error;
      
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      setError(error.message || 'Error al restablecer la contraseña. El enlace puede haber expirado.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBackToLogin = () => {
    navigate('/');
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
        style={{ 
          backgroundImage: 'url("https://images.unsplash.com/photo-1582559146098-c49bdfa68a63?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80")',
          backgroundSize: 'cover'
        }}
      >
        {/* Overlay to darken the image and improve text readability */}
        <div className="absolute inset-0 bg-black opacity-60"></div>
      </div>

      <div className="w-full max-w-md z-10 px-4">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="relative h-40 sm:h-48 bg-gradient-to-r from-primary-light to-primary-dark">
            <div className="absolute inset-0 bg-black opacity-30"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <TruckLogo size={64} className="text-white" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
              <h1 className="text-2xl sm:text-3xl font-bold uppercase">
                COTIZATRIN
              </h1>
              <p className="text-xs sm:text-sm opacity-80">Cotizador de Transporte de Mercancías Pesadas</p>
            </div>
          </div>

          <div className="p-6">
            {verifying ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
                <span className="ml-3 text-gray-600">Verificando enlace...</span>
              </div>
            ) : verificationError ? (
              <div className="py-8">
                <div className="text-center mb-6">
                  <XCircle size={64} className="mx-auto text-red-500 mb-4" />
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">Enlace no válido</h2>
                  <p className="text-gray-600">{verificationError}</p>
                </div>
                <button
                  onClick={handleBackToLogin}
                  className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-4 rounded-md transition duration-200 flex items-center justify-center"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Volver al inicio de sesión
                </button>
              </div>
            ) : success ? (
              <div className="py-8 text-center">
                <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
                <h2 className="text-xl font-semibold text-gray-800 mb-2">¡Contraseña restablecida!</h2>
                <p className="text-gray-600 mb-6">Tu contraseña ha sido actualizada exitosamente.</p>
                <p className="text-gray-500">Serás redirigido al inicio de sesión en unos segundos...</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="flex items-center mb-2">
                    <button
                      onClick={handleBackToLogin}
                      className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <ArrowLeft size={20} className="text-gray-600" />
                    </button>
                    <h2 className="text-2xl font-semibold text-gray-800">Nueva Contraseña</h2>
                  </div>
                  {email && (
                    <p className="text-gray-600 text-sm mt-1">
                      Crea una nueva contraseña para <strong>{email}</strong>
                    </p>
                  )}
                </div>
                
                {error && (
                  <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                    {error}
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="mb-5">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Nueva Contraseña
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock size={16} className="text-gray-400" />
                      </div>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Ingresa tu nueva contraseña"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={16} className="text-gray-400" />
                        ) : (
                          <Eye size={16} className="text-gray-400" />
                        )}
                      </button>
                    </div>
                    
                    {/* Password strength indicators */}
                    <div className="mt-2 grid grid-cols-1 gap-1">
                      <div className="flex items-center text-xs">
                        {passwordStrength.length ? 
                          <CheckCircle size={14} className="text-green-500 mr-1 flex-shrink-0" /> : 
                          <XCircle size={14} className="text-red-500 mr-1 flex-shrink-0" />
                        }
                        <span className={passwordStrength.length ? "text-green-600" : "text-gray-500"}>
                          Mínimo 8 caracteres
                        </span>
                      </div>
                      <div className="flex items-center text-xs">
                        {passwordStrength.uppercase ? 
                          <CheckCircle size={14} className="text-green-500 mr-1 flex-shrink-0" /> : 
                          <XCircle size={14} className="text-red-500 mr-1 flex-shrink-0" />
                        }
                        <span className={passwordStrength.uppercase ? "text-green-600" : "text-gray-500"}>
                          Al menos una letra mayúscula
                        </span>
                      </div>
                      <div className="flex items-center text-xs">
                        {passwordStrength.number ? 
                          <CheckCircle size={14} className="text-green-500 mr-1 flex-shrink-0" /> : 
                          <XCircle size={14} className="text-red-500 mr-1 flex-shrink-0" />
                        }
                        <span className={passwordStrength.number ? "text-green-600" : "text-gray-500"}>
                          Al menos un número
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmar Contraseña
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Shield size={16} className="text-gray-400" />
                      </div>
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                          confirmPassword && password !== confirmPassword
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300'
                        }`}
                        placeholder="Confirma tu contraseña"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={16} className="text-gray-400" />
                        ) : (
                          <Eye size={16} className="text-gray-400" />
                        )}
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="mt-1 text-xs text-red-600">Las contraseñas no coinciden</p>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading || !isPasswordValid() || password !== confirmPassword}
                    className={`w-full py-3 px-4 rounded-md transition duration-200 flex items-center justify-center font-semibold ${
                      loading || !isPasswordValid() || password !== confirmPassword
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-primary hover:bg-primary-dark text-white'
                    }`}
                  >
                    {loading ? (
                      <span className="animate-pulse">Actualizando...</span>
                    ) : (
                      <span>Restablecer Contraseña</span>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
        
        <div className="mt-4 text-center text-white text-sm">
          © 2025 Cotizatrin. Todos los derechos reservados.
          <div className="mt-2 text-xs text-gray-300">
            <p>+52 (55) 1234-5678 | info@cotizatrin.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;